import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAmericana, updateAmericana } from '../api/americanas';
import Breadcrumb from '../components/Breadcrumb';
import 'font-awesome/css/font-awesome.min.css';
import './EditAmericana.css';

const toDateTimeLocalValue = (value) => {
    if (!value) return '';
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        const tzOffset = date.getTimezoneOffset() * 60000;
        const localISOTime = new Date(date.getTime() - tzOffset).toISOString();
        return localISOTime.slice(0, 16);
    } catch (e) {
        return '';
    }
};

const extractMaxPlayers = (americana) => {
    if (!americana) return '';
    const candidates = [americana.maxPlayers, americana.max, americana.capacity];
    for (const value of candidates) {
        const num = Number(value);
        if (Number.isFinite(num) && num > 0) {
            return String(num);
        }
    }
    return '';
};

export default function EditAmericana() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [dateTime, setDateTime] = useState('');
    const [location, setLocation] = useState('');
    const [maxPlayers, setMaxPlayers] = useState('');
    const [notes, setNotes] = useState('');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const loadAmericana = useCallback(async () => {
        setError(null);
        setLoading(true);
        try {
            const data = await getAmericana(id);
            setName(data?.name || '');
            setDateTime(toDateTimeLocalValue(data?.dateTime));
            setLocation(data?.location || '');
            setMaxPlayers(extractMaxPlayers(data));
            setNotes(data?.notes || data?.description || '');
        } catch (e) {
            setError(e?.response?.data?.message || 'No se pudo cargar la americana');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadAmericana();
    }, [loadAmericana]);

    const onSubmit = async (event) => {
        event.preventDefault();
        setError(null);

        if (!name || !dateTime || !maxPlayers) {
            setError('Nombre, fecha/hora y cupo son obligatorios');
            return;
        }

        const maxPlayersValue = Number(maxPlayers);
        if (!Number.isFinite(maxPlayersValue) || maxPlayersValue <= 0) {
            setError('El cupo debe ser un número positivo');
            return;
        }

        setSaving(true);
        try {
            const localDate = new Date(dateTime);
            const isoDate = new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000).toISOString();
            await updateAmericana(id, {
                name,
                dateTime: isoDate,
                location: location || undefined,
                maxPlayers: maxPlayersValue,
                notes: notes || undefined,
            });
            navigate(`/americanas/${id}`);
        } catch (e) {
            setError(e?.response?.data?.message || 'Error actualizando la americana');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="edit-page-wrapper">
                <div className="loading-wrapper">
                    <div className="spinner"></div>
                    <p>Cargando datos...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="edit-page-wrapper">
            <Breadcrumb dark={false} crumbs={[
                { label: 'Americanas', to: '/americanas' },
                { label: name || 'Americana', to: `/americanas/${id}` },
                { label: 'Editar' },
            ]} />

            <div className="edit-card">
                <div className="form-header">
                    <h1>Editar Americana</h1>
                    <p>Modifica los detalles del evento</p>
                </div>

                {error && (
                    <div className="error-banner">
                        <i className="fa fa-exclamation-circle"></i> {error}
                    </div>
                )}

                <form onSubmit={onSubmit} className="edit-form">
                    <div className="form-group">
                        <label>Nombre del Evento</label>
                        <input
                            type="text"
                            className="form-control"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Fecha y Hora</label>
                        <input
                            type="datetime-local"
                            className="form-control"
                            value={dateTime}
                            onChange={(e) => setDateTime(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Ubicación</label>
                        <input
                            type="text"
                            className="form-control"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Cupo de Jugadoras</label>
                        <input
                            type="number"
                            className="form-control"
                            value={maxPlayers}
                            onChange={(e) => setMaxPlayers(e.target.value)}
                            min="2"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Notas</label>
                        <textarea
                            className="form-control"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Añadir notas..."
                        />
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn-cancel" onClick={() => navigate(`/americanas/${id}`)} disabled={saving}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn-submit" disabled={saving}>
                            {saving ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}